import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAtomValue, useStore } from 'jotai';
import request from 'lib/request';
import type { GunBarrelResponse } from 'components/charts/gun-barrel-chart/types';
import {
  compositeRefreshTriggerAtom,
  geometryRefreshAtom,
  slotValuationRefreshTriggerAtom,
} from '../atoms';
import {
  tcInventoryGunBarrelScopeAtom,
  tcSelectedDevAreaAtom,
} from '../type-curve-filters/atoms';
import { buildGunBarrelRequest } from '../gun-barrel-request';
import {
  gunBarrelStateAtoms,
  EMPTY_GUN_BARREL_STATE,
  builderSectionCrossAtom,
} from './atoms';
import type { InventorySurface } from './surface';

const GUN_BARREL_PATH = '/queries/gun_barrel';

/**
 * Owns ONE surface's inventory `POST /queries/gun_barrel` fetch, writing
 * `gunBarrelStateAtoms[surface]`. The Main Page mounts `'main'` (always) and the
 * Unit Builder mounts `'builder'` (only while open); each reads its own copy, so
 * a builder edit never touches the main chart.
 *
 * The request is formation-agnostic (no `formationName`): the backend scopes by
 * `inventoryFormationPackageIds` + unit and returns every formation's slots, so
 * one call serves the surface and each highlights its own formation client-side.
 *
 * Seed reuse: on its first run, if the atom already holds this exact request
 * `signature` settled (deep-copied from the Main surface when the builder opened),
 * the POST is skipped. Re-fires on `geometryRefreshAtom` (either action —
 * restamps the slots on both surfaces).
 *
 * Inactive off an inventory surface (`tcInventoryGunBarrelScopeAtom` null) — the
 * plain Type Curve page keeps fetching inside `TypeCurveGunBarrel`.
 *
 * @param {InventorySurface} surface - 'main' or 'builder'.
 * @param {number | null} typeCurveId - The selected leaf's type-curve header
 *   details id; `null` while the header is still loading (fetch held).
 * @param {number | null} structureGridCollectionId - The header's default
 *   structure-grid collection id (drives the formation bands), or `null`.
 */
export function useInventoryGunBarrelFor(
  surface: InventorySurface,
  typeCurveId: number | null,
  structureGridCollectionId: number | null
): void {
  const store = useStore();
  const gbAtom = gunBarrelStateAtoms[surface];
  const inventoryScope = useAtomValue(tcInventoryGunBarrelScopeAtom);
  const selectedDevArea = useAtomValue(tcSelectedDevAreaAtom);
  const geometryRefresh = useAtomValue(geometryRefreshAtom);
  // A persisted valued/notes change restamps the flag here without dragging in
  // the whole geometry fan-out.
  const valuationRefresh = useAtomValue(slotValuationRefreshTriggerAtom);
  const compositeRefresh = useAtomValue(compositeRefreshTriggerAtom);
  // Builder only: its live edited section line. As the user reshapes the boundary
  // this overrides the selection's cross-section so the builder gun barrel
  // re-fetches for the new section. `null` (or on the main surface) → use the
  // selection's own cross-section.
  const builderSectionCross = useAtomValue(builderSectionCrossAtom);

  const body = useMemo(() => {
    if (!inventoryScope || typeCurveId === null) return null;
    const effectiveDevArea =
      selectedDevArea && surface === 'builder' && builderSectionCross
        ? { ...selectedDevArea, crossSectionEndPoints: builderSectionCross }
        : selectedDevArea;
    return buildGunBarrelRequest({
      selectedDevArea: effectiveDevArea,
      inventoryScope,
      structureGridCollectionId,
      typeCurveId,
      // Formation-agnostic — the builder's arrows and the sidebar leaf highlight
      // client-side without refetching.
      highlightedFormation: null,
    });
  }, [
    inventoryScope,
    selectedDevArea,
    builderSectionCross,
    surface,
    structureGridCollectionId,
    typeCurveId,
  ]);

  const signature = body ? JSON.stringify(body) : null;
  const abortRef = useRef<AbortController | null>(null);
  const lastKeyRef = useRef<string | null>(null);

  const fetchGunBarrel = useCallback(async () => {
    if (!body || signature === null) {
      store.set(gbAtom, EMPTY_GUN_BARREL_STATE);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Keep the previous chart on a refetch (only flip `loading`).
    store.set(gbAtom, (prev) => ({
      ...prev,
      loading: true,
      error: null,
      signature,
    }));

    try {
      const response = await request.post<GunBarrelResponse, unknown>(
        GUN_BARREL_PATH,
        body,
        controller
      );
      if (controller.signal.aborted || response.type === 'abort') return;
      if (response.type === 'fatal') {
        store.set(gbAtom, {
          loading: false,
          data: null,
          error: 'Failed to load Gun Barrel',
          signature,
        });
        return;
      }
      if (response.type === 'error') {
        store.set(gbAtom, {
          loading: false,
          data: null,
          error: 'Failed to load Gun Barrel',
          signature,
        });
        return;
      }
      if (response.type !== 'success') return;
      store.set(gbAtom, {
        loading: false,
        data: response.data,
        error: null,
        signature,
      });
    } catch {
      if (!controller.signal.aborted) {
        store.set(gbAtom, {
          loading: false,
          data: null,
          error: 'Failed to load Gun Barrel',
          signature,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, store, gbAtom]);

  useEffect(() => {
    const key = `${
      signature ?? ''
    }|${geometryRefresh}|${valuationRefresh}|${compositeRefresh}`;
    if (lastKeyRef.current === key) return;
    const firstRun = lastKeyRef.current === null;
    lastKeyRef.current = key;
    // Seed reuse: on the builder's first run the atom may already hold this exact
    // signature settled (deep-copied from main on open) — adopt it, skip the POST.
    if (firstRun && signature !== null) {
      const current = store.get(gbAtom);
      if (current.data && current.signature === signature && !current.loading)
        return;
    }
    void fetchGunBarrel();
  }, [
    signature,
    geometryRefresh,
    valuationRefresh,
    compositeRefresh,
    fetchGunBarrel,
    store,
    gbAtom,
  ]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );
}
